import React, { useEffect, useState } from 'react';
import { legacyFincetApi } from '../services/legacyFincetApi';
import { ExternalStatus, STATUS_OPTIONS } from '../types/types';

// Type d'un fournisseur legacy
export interface LegacyFournisseurData {
	legacyId: number;
	label: string;
	externalKey?: string;
	externalStatus: ExternalStatus;
	corelysId?: number;
}

interface Props {
	token: string;
}

export default function LegacyFournisseursTable({ token }: Props) {
	const [fournisseurs, setFournisseurs] = useState<LegacyFournisseurData[]>([]);
	const [loading, setLoading] = useState(true);

	// Chargement des fournisseurs depuis l'API
	useEffect(() => {
		legacyFincetApi
			.list(token)
			.then(setFournisseurs)
			.finally(() => setLoading(false));
	}, [token]);

	// Changer le status dans le state
	const handleStatusChange = (index: number, newStatus: ExternalStatus) => {
		const copy = [...fournisseurs];
		copy[index].externalStatus = newStatus;
		setFournisseurs(copy);
	};

	// Actions
	const handleImport = (legacyId: number) =>
		legacyFincetApi
			.import(legacyId, token)
			.then((data) => {
				if (data.corelysId) {
					alert(`Importé avec succès, Corelys ID: ${data.corelysId}`);

					// Met à jour le state
					setFournisseurs((prev) =>
						prev.map((f) =>
							f.legacyId === legacyId
								? {
										...f,
										externalKey: `${data.corelysId}`,
										externalStatus: 'IMPORTED',
									}
								: f
						)
					);
				}
			})
			.catch((err) => alert(`Erreur réseau: ${err}`));

	const handleAssociate = (legacyId: number) => {
		const corelysId = prompt('ID Corelys ?');
		if (corelysId) {
			legacyFincetApi
				.update(legacyId, { externalKey: `${corelysId}` }, token)
				.then(() => {
					alert(`Associé à ${corelysId}`);
					// Met à jour le state
					setFournisseurs((prev) =>
						prev.map((f) =>
							f.legacyId === legacyId
								? {
										...f,
										externalKey: `${corelysId}`,
										externalStatus: 'MANUALLY_LINKED',
									}
								: f
						)
					);
				});
		}
	};

	if (loading) return <p>Chargement...</p>;

	return (
		<table className="w-full border border-gray-300">
			<thead className="bg-gray-100">
				<tr>
					<th className="p-2 border">Label</th>
					<th className="p-2 border">Legacy Key</th>
					<th className="p-2 border">Local Key</th>
					<th className="p-2 border">Status</th>
					<th className="p-2 border">Actions</th>
				</tr>
			</thead>
			<tbody>
				{fournisseurs
					.filter((f) => f.label) // filtre les fournisseurs sans label
					.sort((a, b) => a.label.localeCompare(b.label)) // trie par label
					.map((f, idx) => {
						let rowClass = 'hover:bg-gray-50'; // classe par défaut
						if (
							f.externalStatus === 'IMPORTED' ||
							f.externalStatus === 'MANUALLY_LINKED'
						)
							rowClass += ' bg-green-100';
						else if (f.externalStatus === 'NOT_IMPORTED')
							rowClass += ' bg-yellow-100';
						else if (f.externalStatus === 'IGNORED') rowClass += ' bg-red-100';

						return (
							<tr key={f.legacyId} className={rowClass}>
								<td className="p-2 border">{f.label}</td>
								<td className="p-2 border">{f.legacyId}</td>
								<td className="p-2 border">
									{f.externalKey ? (
										<a
											href={`/app/tiers/${f.externalKey}/manage`}
											className="text-blue-600 underline"
										>
											{f.externalKey}
										</a>
									) : (
										'-'
									)}
								</td>
								<td className="p-2 border">
									<select
										value={f.externalStatus}
										onChange={(e) =>
											handleStatusChange(idx, e.target.value as ExternalStatus)
										}
									>
										{STATUS_OPTIONS.map((s) => (
											<option key={s} value={s}>
												{s}
											</option>
										))}
									</select>
								</td>
								<td className="p-2 border flex gap-2">
									{(f.externalStatus === 'NOT_IMPORTED' ||
										f.externalStatus === 'IGNORED') && (
										<>
											<button onClick={() => handleImport(f.legacyId)}>
												Importer
											</button>
											<button onClick={() => handleAssociate(f.legacyId)}>
												Associer
											</button>
										</>
									)}
								</td>
							</tr>
						);
					})}
			</tbody>
		</table>
	);
}
